import { PropsWithChildren, useMemo, useState } from 'react';

import { Button } from '@bambi/component-library/src/components/Button/Button';
import { DefaultFormFieldLayout } from '@bambi/component-library/src/components/forms/DefaultFormFieldLayout/DefaultFormFieldLayout';
import { FormFieldContainer } from '@bambi/component-library/src/components/forms/layout/FormFieldContainer';
import { FormFieldSet } from '@bambi/component-library/src/components/forms/layout/FormFieldSet';
import { Select } from '@bambi/component-library/src/components/inputs/Select/Select';
import { LoadingIndicator } from '@bambi/component-library/src/components/LoadingIndicator/LoadingIndicator';
import { useFormContext } from 'react-hook-form';

import { useFeature } from 'src/app/FeatureToggle';
import { states } from 'src/common/data/states';
import {
  useDriversListQuery,
  usePayersListQuery,
} from 'src/common/external/bambi-api/bambiApi';
import { FormField } from 'src/common/FormField';
import { FormFieldController } from 'src/common/FormField/FormFieldController';
import { MultiSelectPayerField } from 'src/common/MultiSelectPayerContextField';
import {
  formatFullName,
  formatFullNameIgnoreDeletedAt,
} from 'src/common/util/formatFullName';

import { FormValues } from '../FormValues';
import { useNonDriverMembers } from './useNonDriverMembers';

import type { SelectOption } from '@bambi/component-library/src/components/inputs/Select/SelectOption/SelectOption';

interface DriverProfileFormStep1Props extends PropsWithChildren {
  nextButtonLabel?: string;
  validate?: boolean;
  onNextClick: () => void;
  onCancel: () => void;
}
export function DriverProfileFormStep1({
  nextButtonLabel,
  validate = true,
  onNextClick,
  onCancel,
  children,
}: DriverProfileFormStep1Props) {
  const { isEnabled: isPayerAllowedVehiclesAndDriversEnabled } = useFeature(
    'payer allowed vehicles and drivers'
  );

  const [searchQuery, setSearchQuery] = useState<string>();
  const { members } = useNonDriverMembers(searchQuery);
  const { data: deletedDrivers, isLoading: isLoadingDeletedDrivers } =
    useDriversListQuery({
      search: searchQuery,
      deletedObjects: 'include',
    });

  const form = useFormContext<FormValues>();

  const profileOptions = useMemo(() => {
    const options: SelectOption<string>[] = [];
    members.forEach((member) => {
      options.push({
        value: member.id,
        label: formatFullNameIgnoreDeletedAt(member),
      });
    });
    deletedDrivers?.results.forEach((driver) => {
      if (!driver.deleted_at) {
        return;
      }
      options.push({
        value: driver.id,
        label: formatFullNameIgnoreDeletedAt(driver),
      });
    });
    return options;
  }, [deletedDrivers?.results, members]);

  const onNavigateToStep2 = async () => {
    if (!validate) {
      return onNextClick();
    }

    const isValid = await form.trigger();
    if (isValid) {
      onNextClick();
    }
  };

  const getSelectedOption = (val: string): SelectOption => {
    const member = members.find((member) => member.id === val);
    const driver = deletedDrivers?.results.find((driver) => driver.id === val);

    if (member) {
      return {
        value: member.id,
        label: formatFullNameIgnoreDeletedAt(member),
      };
    }

    if (driver) {
      return {
        value: driver.id,
        label: formatFullNameIgnoreDeletedAt(driver),
      };
    }

    const { member_id, first_name, last_name } = form.getValues();
    return {
      value: member_id,
      label: formatFullName({ first_name, last_name }),
    };
  };

  const editable = !form.getValues().deleted_at;

  const { data: payersWithAllowedDrivers } = usePayersListQuery(
    {
      payerSettingsAllowOnlySpecificDrivers: true,
    },
    {
      skip: !isPayerAllowedVehiclesAndDriversEnabled,
    }
  );

  const showAllowedPayers = useMemo(
    () =>
      isPayerAllowedVehiclesAndDriversEnabled &&
      !!payersWithAllowedDrivers?.results.length,
    [isPayerAllowedVehiclesAndDriversEnabled, payersWithAllowedDrivers]
  );

  return (
    <div className="mt-4 flex flex-col gap-4">
      {isLoadingDeletedDrivers ? (
        <div className="items-center">
          <LoadingIndicator />
        </div>
      ) : (
        <FormFieldController
          name="member_id"
          rules={{ required: 'Driver is required' }}
        >
          {({ field, fieldState }) => {
            return (
              <FormFieldContainer colSpan={12}>
                <DefaultFormFieldLayout
                  label="Select a driver"
                  error={fieldState.error?.message?.toString()}
                  inputId="member_id"
                >
                  <Select
                    query={searchQuery}
                    searchable
                    placeholder="Select a member to add as a driver"
                    options={
                      field.value
                        ? [getSelectedOption(field.value)]
                        : profileOptions
                    }
                    disabled={!!form.getValues().id}
                    selected={
                      field.value ? getSelectedOption(field.value) : undefined
                    }
                    onChange={(value) => {
                      const member = members.find(
                        (member) => member.id === value?.value
                      );
                      form.setValue('first_name', member?.first_name || '');
                      form.setValue('last_name', member?.last_name || '');
                      field.onChange(value?.value);
                    }}
                    setQuery={setSearchQuery}
                    testIdSuffix="driver-select"
                  />
                </DefaultFormFieldLayout>
              </FormFieldContainer>
            );
          }}
        </FormFieldController>
      )}
      <FormFieldSet
        legend="License Information"
        Fields={
          <>
            <FormFieldController
              name="license_number"
              rules={{
                required: 'License Number is required',
              }}
            >
              {({ field, fieldState }) => {
                return (
                  <FormField
                    disabled={!editable}
                    label="License Number"
                    error={fieldState.error?.message?.toString()}
                    type="text"
                    inputProps={{
                      id: 'license-number',
                      placeholder: 'e.g. 123455678',
                      ...field,
                      disabled: !editable,
                      readOnly: !editable,
                      onChange: (e) => {
                        const cleanedValue = e.target.value.replace(
                          /[-\s]/g,
                          ''
                        );
                        field.onChange(cleanedValue);
                      },
                    }}
                    helperText="Enter without dashes or spaces"
                  />
                );
              }}
            </FormFieldController>
            <FormFieldController
              name="license_dob"
              rules={{ required: 'Date of Birth is required' }}
            >
              {({ field, fieldState }) => {
                return (
                  <FormField
                    disabled={!editable}
                    label="Driver Date of Birth"
                    error={fieldState.error?.message?.toString()}
                    type="date"
                    inputProps={{
                      ...field,
                      disabled: !editable,
                      readOnly: !editable,
                      id: 'date-of-birth',
                      placeholder: 'Select a date',
                    }}
                  />
                );
              }}
            </FormFieldController>
            <FormFieldController
              name="license_state"
              rules={{ required: 'Issued State is required' }}
            >
              {({ field, fieldState }) => {
                return (
                  <FormFieldContainer colSpan={6}>
                    <DefaultFormFieldLayout
                      label="Issued State"
                      error={fieldState.error?.message?.toString()}
                      inputId="issued-state"
                    >
                      <Select
                        disabled={!editable}
                        placeholder="Select State"
                        options={states}
                        {...field}
                        onChange={(value?: SelectOption) => {
                          field.onChange(value?.value);
                        }}
                        error={fieldState.error?.message?.toString()}
                      />
                    </DefaultFormFieldLayout>
                  </FormFieldContainer>
                );
              }}
            </FormFieldController>
            <FormFieldController
              name="license_expiration_date"
              rules={{ required: 'Expiration Date is required' }}
            >
              {({ field, fieldState }) => {
                return (
                  <FormField
                    disabled={!editable}
                    label="Expiration Date"
                    error={fieldState.error?.message?.toString()}
                    type="date"
                    inputProps={{
                      ...field,
                      disabled: !editable,
                      readOnly: !editable,
                      id: 'expiration-date',
                      placeholder: 'Select a date',
                    }}
                  />
                );
              }}
            </FormFieldController>
            <FormFieldController name="can_be_scheduled">
              {({ field, fieldState }) => {
                return (
                  <FormField
                    disabled={!editable}
                    label="Is this driver active?"
                    error={fieldState.error?.message?.toString()}
                    type="switch"
                    inputProps={{
                      disabled: !editable,
                      readOnly: !editable,
                      id: 'can_be_scheduled',
                      onChange: (value) => {
                        field.onChange(value);
                      },
                      value: '',
                      checked: field.value,
                    }}
                  />
                );
              }}
            </FormFieldController>
          </>
        }
      />
      {showAllowedPayers && (
        <FormFieldSet
          legend="Allowed Payers"
          Fields={
            <div className="col-span-12 mb-4">
              <MultiSelectPayerField
                fieldPath="allowed_payer_ids"
                canSelectAll={false}
                payerFilters={{
                  payerSettingsAllowOnlySpecificDrivers: true,
                }}
              />
            </div>
          }
        />
      )}
      {children}
      <div className="grid grid-cols-2 gap-2">
        <Button
          onClick={() => {
            onCancel();
          }}
        >
          Cancel
        </Button>
        <Button
          className="w-full flex-grow"
          variant="primary"
          onClick={onNavigateToStep2}
        >
          {nextButtonLabel ? nextButtonLabel : 'Next'}
        </Button>
      </div>
    </div>
  );
}
